// Data Pipeline — flagship live/history monitor of the whole data plane:
// page-hd → KPI strip → FLOW DIAGRAM (hero) → CONTEXT PANEL (inline) →
// LIVE: payload inspector / HISTORY: charts → mono footer.
//
// Port of the approved Claude Design mockup (design-bundle page.js /
// context-panel.js / inspector.js / history.js) bound to the real /flow/*
// contract. Geometry + derivation live in lib/flow-layout; chip vocabulary +
// feed merge helpers in lib/flow-masks; the diagram in components/flow-diagram.
//
// HONESTY DEVIATION from the mockup: when a production scope is active the
// conversion cells (queue, sent/delivered) and the encore/activation diagram
// stats stay GLOBAL — the conversion queue keeps no production, and a
// scheduled ingest run covers every production at once. Those cells/sub-lines
// carry a mono '· global' marker instead.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Icon, Icons } from '@/components/primitives';
import { SyncDot } from '@/components/sync-pill';
import { LineageTrace } from '@/components/lineage';
import { dpTraceDerive, isBareUuid, kindFromCorrelationId, normalizeCorrelationId } from '@/lib/flow-lineage';
import { pillClass, type Band } from '@/lib/system-metrics';
import { Dropdown, DateFilter, DATE_PRESETS, SearchableDropdown, resolveRange } from '@/components/dropdowns';
import type { DropdownOption } from '@/components/dropdowns';
import { StackedMetricChart, bucketLabel } from '@/components/charts';
import { CPChartDock } from '@/components/copilot';
import type { ChartExplainSel } from '@/components/copilot';
import { DPTick, FlowDiagram, useNowMs } from '@/components/flow-diagram';
import { MutationErrorLine, QueryErrorNote } from '@/components/error-panel';
import { ScrollTable } from '@/components/scroll-table';
import { usePhone } from '@/lib/responsive';
import { useCan } from '@/auth/capabilities';
import { useMe } from '@/auth/useMe';
import { ApiError } from '@/api/errors';
import { chartDockResetKey, chartExplainRequest } from '@/lib/copilot';
import {
  useCopilotStatus, useFlowFeed, useFlowHistory, useFlowLineage, useFlowSnapshot,
  useFlowTicketsRecent, useProductions, useResolveBareTrace, useRevealFlowEvent, useRevealTicket,
} from '@/api/hooks';
import type { FlowHistoryGranularity, FlowHistoryQuery } from '@/api/hooks';
import { FLOW_FEED_POLL_MS, FLOW_SNAPSHOT_POLL_MS } from '@/api/resources';
import {
  DP_EDGES, DP_NODES, DP_NODE_GROUP,
  cronWords, dpAge, dpClock, dpDur, dpHealthChip, dpHealthColor, dpK, dpN, dpPct, dpRate, num, dpDerive, targetCounts,
} from '@/lib/flow-layout';
import type { DpNodeId } from '@/lib/flow-layout';
import {
  fmtDate, fmtDateShort, fmtStampEt, nyEndOfDayMsExclusive, nyMidnightMs, nyParts, nyStartOfDayIso, nyStartOfDayMs, withEt,
} from '@/lib/datetime';
import {
  bucketFloorStates, bucketize, chipTip, decodeSignalMask, enumerateBuckets, fcState, identityChips, isEventType,
  isPurchase, piiChips, priceChips, ratePct, revealMsLeft, revealSecondsLeft, ticketChips,
} from '@/lib/flow-masks';
import type { ChipModel, FloorState } from '@/lib/flow-masks';
import type {
  ConversionsHistoryResponse,
  FlowEventRedacted,
  FlowLivePage,
  FlowRevealResponse,
  PipelineFlowSnapshot,
  TicketsHistoryResponse,
  WebEventsHistoryResponse,
} from '@/api/types';
import type {
  FieldCheck,
  HistoryBucket,
  PixelConsent,
  TicketFlowRedacted,
  TicketRevealResponse,
} from '@/api/types.gen';
import { PageActions } from '@/components/page-actions';
import { kpiScrollRegionProps } from '@/components/responsive-kpis';

// The eye glyph exists only in the design set — house Icon wrapper, same stroke.
const eyeIcon = (
  <Icon size={12} d={<><path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z"/><circle cx="8" cy="8" r="2"/></>}/>
);

// ─── chip vocabulary (models from lib/flow-masks, DOM per design chips.js) ───

function Chip({ chip }: { chip: ChipModel }) {
  return (
    <span className={'dp-chip ' + fcState(chip.fc) + (chip.hashed ? ' hash' : '')} title={chipTip(chip)}>
      {chip.letter}
    </span>
  );
}

function PiiChips({ ev }: { ev: FlowEventRedacted }) {
  const { chips } = piiChips(ev.pii, ev.hashed_pii);
  return <span className="dp-chips">{chips.map((c) => <Chip key={c.letter} chip={c}/>)}</span>;
}

function PriceChips({ ev }: { ev: FlowEventRedacted }) {
  const chips = priceChips(ev);
  if (!chips) return <span className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, opacity: 0.5 }}>—</span>;
  return <span className="dp-chips">{chips.map((c) => <Chip key={c.letter} chip={c}/>)}</span>;
}

function IdentityChips({ ev }: { ev: FlowEventRedacted }) {
  return <span className="dp-chips">{identityChips(ev.identity).map((c) => <Chip key={c.label} chip={c}/>)}</span>;
}

// Person-resolution dot: ✓ resolved / — not matched / ⋯ pending
function MatchDot({ resolved }: { resolved: boolean | null }) {
  if (resolved === true) return <span className="dp-match yes" title="PERSON — RESOLVED">✓</span>;
  if (resolved === null) return <span className="dp-match pending" title="PERSON — PENDING (in flight to resolver)">⋯</span>;
  return <span className="dp-match no" title="PERSON — NOT MATCHED">—</span>;
}

function ConsentFlags({ consent }: { consent: PixelConsent }) {
  return (
    <span style={{ display: 'inline-flex', gap: 10 }}>
      <span className={'dp-flag ' + (consent.ads ? 'on' : 'warn')}><span className="d"/>ADS {consent.ads ? 'GRANTED' : 'DENIED'}</span>
      <span className={'dp-flag ' + (consent.analytics ? 'on' : 'warn')}><span className="d"/>ANALYTICS {consent.analytics ? 'GRANTED' : 'DENIED'}</span>
    </span>
  );
}

// 13-char conversion signal mask → labeled chips (§6.3 legend)
function MaskChips({ mask }: { mask: string | null }) {
  if (!mask) return <span className="muted" style={{ fontSize: 10.5 }}>No conversion signals — event not enqueued.</span>;
  return (
    <span className="dp-mask">
      {decodeSignalMask(mask).map((c) => (
        <span key={c.letter} className={'dp-mask-chip' + (c.on ? '' : ' off')}
          title={c.label.toUpperCase() + ' — ' + (c.on ? 'SIGNAL PRESENT' : 'NOT PRESENT')}>
          <b>{c.letter}</b>{c.label}
        </span>
      ))}
    </span>
  );
}

function TicketChips({ t }: { t: TicketFlowRedacted }) {
  const { contact, money } = ticketChips(t);
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <span className="dp-chips">{contact.map((c) => <Chip key={c.letter} chip={c}/>)}</span>
      <span className="dp-chips">{money.map((c) => <Chip key={c.letter} chip={c}/>)}</span>
    </span>
  );
}

// ─── small header pieces ─────────────────────────────────────────────────────

function ModeSeg({ mode, onChange }: { mode: 'live' | 'history'; onChange: (m: 'live' | 'history') => void }) {
  return (
    <div className="seg">
      <button className={mode === 'live' ? 'on' : ''} onClick={() => onChange('live')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: mode === 'live' ? 'var(--live)' : 'var(--ink-3)' }}/>LIVE
      </button>
      <button className={mode === 'history' ? 'on' : ''} onClick={() => onChange('history')}>HISTORY</button>
    </div>
  );
}

// TRACE search box — type/paste a correlation_id (or a bare id we normalize)
// to activate E2E lineage mode. URL is the source of truth (see setTrace).
function TraceBox({ trace, onTrace }: { trace: string | null; onTrace: (id: string | null) => void }) {
  const [input, setInput] = useState(trace ?? '');
  // null = no hint; otherwise which inline hint to show.
  const [hint, setHint] = useState<null | 'prefix' | 'resolving' | 'notfound'>(null);
  // Set to the bare UUID when it resolved in BOTH lanes → user disambiguates.
  const [bothUuid, setBothUuid] = useState<string | null>(null);
  const resolveBare = useResolveBareTrace();
  // Monotonic token: a probe result only acts if no newer submit / clear / nav
  // has superseded it (resolveBare issues real parallel network fetches). The
  // cleanup bump also invalidates in-flight probes on UNMOUNT — onBlur fires
  // submit() when a nav click steals focus, and a late resolution must not
  // navigate the user back into TRACE mode from a dead screen.
  const seqRef = useRef(0);
  useEffect(() => {
    seqRef.current++; setInput(trace ?? ''); setHint(null); setBothUuid(null);
    return () => { seqRef.current++; };
  }, [trace]);

  const go = (id: string) => { setHint(null); setBothUuid(null); if (id !== trace) onTrace(id); };
  const clearHints = () => { if (hint || bothUuid) { setHint(null); setBothUuid(null); } };

  const submit = async () => {
    const myseq = ++seqRef.current;
    const raw = input.trim();
    setBothUuid(null);
    if (!raw) { setHint(null); if (trace) onTrace(null); return; }
    // Prefixed (web:/tkt:/unk:) or a non-UUID slug → deterministic lane.
    if (!isBareUuid(raw)) {
      const id = normalizeCorrelationId(raw);
      if (!id) { setHint('prefix'); return; }
      go(id);
      return;
    }
    // Bare UUID is ambiguous (ticket_ids AND web_event_ids are both UUIDs):
    // probe both lanes and route to whichever actually has lineage.
    setHint('resolving');
    const { ticket, web } = await resolveBare(raw);
    if (myseq !== seqRef.current) return; // superseded by a newer submit / clear / nav
    if (ticket && web) { setHint(null); setBothUuid(raw); }
    else if (ticket) go('tkt:' + raw);
    else if (web) go('web:' + raw);
    else setHint('notfound');
  };

  return (
    <div className="dp-trace-box" title="Trace one item's E2E lineage by correlation_id">
      <span className="dp-trace-ico">{Icons.search}</span>
      <input
        className="dp-trace-input"
        placeholder="Trace  web:… / tkt:… / paste an id"
        value={input}
        spellCheck={false}
        onChange={(e) => { setInput(e.target.value); clearHints(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') void submit(); if (e.key === 'Escape') { seqRef.current++; setInput(trace ?? ''); clearHints(); } }}
        onBlur={() => void submit()}
      />
      {(input || trace) && (
        <button
          type="button" className="dp-trace-x" title="Clear trace"
          onMouseDown={(e) => { e.preventDefault(); seqRef.current++; setInput(''); setHint(null); setBothUuid(null); onTrace(null); }}
        >
          {Icons.x}
        </button>
      )}
      {hint === 'prefix' && <span className="dp-trace-hint">prefix web: or tkt:</span>}
      {hint === 'resolving' && <span className="dp-trace-hint">searching both lanes…</span>}
      {hint === 'notfound' && <span className="dp-trace-hint">no lineage in the ticket or web lane</span>}
      {bothUuid && (
        <span className="dp-trace-hint">
          found in both:{' '}
          <button type="button" className="dp-trace-link" onMouseDown={(e) => { e.preventDefault(); go('tkt:' + bothUuid); }}>ticket</button>
          {' · '}
          <button type="button" className="dp-trace-link" onMouseDown={(e) => { e.preventDefault(); go('web:' + bothUuid); }}>web</button>
        </span>
      )}
    </div>
  );
}

// Disabled dd-trigger lookalike — the house DateFilter/Dropdown have no
// disabled state; LIVE mode renders these instead (design: grayed triggers).
function DisabledDd({ label, val, icon, title }: { label: string; val: string; icon?: ReactNode; title: string }) {
  return (
    <div className="dd">
      <button className="dd-trigger" disabled title={title}>
        <span className="lbl">{label}</span>
        {icon && <span style={{ display: 'inline-flex', color: 'var(--ink-2)' }}>{icon}</span>}
        <span className="val">{val}</span>
        <span className="chev">{Icons.chev_d}</span>
      </button>
    </div>
  );
}

// Overall health chip for the page header (sync-pill family + warn variant).
// Wording + hover breakdown live in `dpHealthChip`, beside the node derivation
// whose component coverage it has to stay honest about.
function HealthChip({ snapshot }: { snapshot: PipelineFlowSnapshot }) {
  // Conversions disabled entirely is not a warning condition — only an
  // enabled-but-dark (log_only) pipeline warrants the amber chip.
  const logOnly = snapshot.batch.conversions.enabled && snapshot.batch.conversions.mode === 'log_only';
  const chip = dpHealthChip(snapshot.health.components, logOnly);
  return (
    <div
      className={'sync-pill ' + pillClass(chip.band as Band)}
      style={{ fontSize: 10.5, padding: '4px 9px 4px 8px' }}
      title={chip.tip}
    >
      <SyncDot band={chip.band as Band} style={{ width: 7, height: 7 }}/>
      <span>{chip.label}</span>
    </div>
  );
}

// Conversions delivery-mode badge (context panel + KPI strip).
function ModeBadge({ mode, enabled = true }: { mode: string; enabled?: boolean }) {
  // The batch config default is mode="log_only" even when [conversions] is
  // absent — `enabled` is the authoritative OFF signal, not the mode string.
  const [label, color] = !enabled
    ? ['OFF', 'var(--ink-3)'] as const
    : mode === 'live'
      ? ['LIVE', 'var(--good)'] as const
      : mode === 'log_only'
        ? ['LOG-ONLY', 'var(--warn)'] as const
        : ['OFF', 'var(--ink-3)'] as const;
  return (
    <span className="tag" style={{
      background: `color-mix(in oklch, ${color} 14%, transparent)`,
      color, border: `1px solid color-mix(in oklch, ${color} 40%, transparent)`, fontWeight: 700,
    }}>{label}</span>
  );
}

/** '· global' marker for cells that cannot be scoped to a production. */
function GlobalMark() {
  return (
    <span className="mono" title="Not scoped to the selected production — global value" style={{ color: 'var(--ink-3)' }}>
      {' '}· global
    </span>
  );
}

// ─── KPI strip (§4) — 6 cells, live vs history-totals variants ───────────────

function DPDelta({ cur, prev, invert = false, suffix = '%', partialToday = false }: {
  /** `null`: the rate is unknown (nothing to divide by) — no delta can be drawn. */
  cur: number | null;
  prev: number | null | undefined;
  invert?: boolean;
  suffix?: string;
  /** The range includes today, which the comparison leaves out. */
  partialToday?: boolean;
}) {
  if (cur == null || prev == null || (suffix !== 'pp' && prev === 0)) return null;
  // 'pp' deltas are percentage-POINT differences (the inputs are already %);
  // everything else is a relative % change vs the prior period.
  const d = suffix === 'pp' ? cur - prev : ((cur - prev) / prev) * 100;
  const up = d >= 0;
  const good = invert ? !up : up;
  return (
    <span
      style={{ color: good ? 'var(--good)' : 'var(--bad)', letterSpacing: '.04em' }}
      title={'Completed days only, against the same number of New York days before the range'
        + (partialToday ? '. Today is shown but not counted.' : '.')}
    >
      {up ? '▲ +' : '▼ '}{d.toFixed(1)}{suffix} vs prior period
    </span>
  );
}

type KpiCell = [string, ReactNode, ReactNode];

/** A run's progress line: "running · 42%" (or "queued"). */
function runningLabel(run: { status: string; progress: number | null }): string {
  return run.status === 'running' ? 'running · ' + (run.progress ?? 0) + '%' : run.status;
}

export function KpiStrip({ snapshot, mode, hist, prior, scoped }: {
  snapshot: PipelineFlowSnapshot;
  mode: 'live' | 'history';
  hist: HistDerived | null;
  prior: PriorTotals | null;
  scoped: boolean;
}) {
  const b = snapshot.batch;
  const rows = (src: string) => b.web_events.filter((r) => r.source === src);
  const sum = (rws: typeof b.web_events, k: 'count_5m' | 'count_1h' | 'count_24h') =>
    rws.reduce((s, r) => s + num(r[k]), 0);
  const conv = b.conversions;
  const run = snapshot.last_ingest_run;
  const liveRun = snapshot.running_ingest_run ?? null;
  const rz = b.resolve_1h;
  let cells: KpiCell[];
  if (mode === 'history' && !hist) {
    // Range queries still in flight — never silently fall back to LIVE cells
    // (current 5-min rates under a HISTORY header would be a lie).
    const loadingSub = (
      <span className="mono" style={{ color: 'var(--ink-3)', letterSpacing: '.06em' }}>LOADING RANGE…</span>
    );
    cells = (['Events · range', 'Web share', 'Person match · purchases', 'Tickets', 'Conversions queued', 'Delivered · by event date'] as const)
      .map((l): KpiCell => [l, '—', loadingSub]);
  } else if (mode === 'live' || !hist) {
    const webMin = sum(rows('pixel'), 'count_5m') / 5;
    const srvMin = sum(rows('pixel_sgtm'), 'count_5m') / 5;
    const web24 = sum(rows('pixel'), 'count_24h');
    const srv24 = sum(rows('pixel_sgtm'), 'count_24h');
    // log_only sends nothing: its rows are "logged", shown as such, never as
    // sent; failures are counted apart, never folded into the logged figure.
    const logOnly = conv.mode === 'log_only';
    const meta = targetCounts(conv.by_target.find((t) => t.target === 'meta'), logOnly);
    const ttd = targetCounts(conv.by_target.find((t) => t.target === 'ttd'), logOnly);
    const out24 = meta.delivered_24h + ttd.delivered_24h;
    const fail24 = meta.failed_24h + ttd.failed_24h;
    const purchases = num(rz.purchases_1h);
    const matched = num(rz.purchase_person_1h);
    // A global run's ticket count covers every production: marked when scoped.
    const runGlobal = scoped && run != null && run.production_id == null;
    cells = [
      ['Events / min', dpN(webMin + srvMin), <span>web {dpN(webMin)} · server {dpN(srvMin)}</span>],
      ['Web vs server · 24h', dpPct(web24, web24 + srv24), <span>{dpN(web24)} / {dpN(srv24)} events</span>],
      ['Person match · purchases', dpPct(matched, purchases, 0),
        <span>{dpN(matched)} of {dpN(purchases)} purchases · 1h</span>],
      ['Tickets last run', run && run.tickets_out != null ? dpN(num(run.tickets_out)) : '—',
        <span>
          {run ? <>lag {run.source_lag_hours != null ? num(run.source_lag_hours) : '—'}h · {run.status}</> : 'no finished run'}
          {liveRun && <span title={'In flight: ' + liveRun.trigger}> · {runningLabel(liveRun)}</span>}
          {runGlobal && <GlobalMark/>}
        </span>],
      ['Conversion queue', dpN(num(conv.queue_depth)),
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          oldest {dpDur(conv.oldest_pending_age_secs == null ? null : num(conv.oldest_pending_age_secs))} <ModeBadge mode={conv.mode} enabled={conv.enabled}/>{scoped && <GlobalMark/>}
        </span>],
      [logOnly ? 'Logged · 24h (not sent)' : 'Sent · 24h', dpN(out24),
        <span title="Counted by the time each conversion was sent or logged, not by its event date">
          {logOnly ? dpN(fail24) + ' failed' : dpPct(fail24, out24 + fail24) + ' failed'}{scoped && <GlobalMark/>}
        </span>],
    ];
  } else {
    const t = hist.totals;
    // Deltas compare COMPLETED days only: today is shown in the value, never
    // in the comparison, and the prior window is the same number of New York
    // calendar days immediately before the range.
    const c = hist.comparable;
    const p = prior;
    const today = hist.includesToday;
    // Conversion logs are retention-bounded: when the floor falls inside the
    // prior window its totals are undercounted — a delta would show false
    // growth, so render the plain value with an explanatory marker instead.
    const convTruncNote = (
      <span title="The conversions history floor falls inside the compared windows: their counts are short" style={{ color: 'var(--ink-3)', letterSpacing: '.04em' }}>
        — prior truncated
      </span>
    );
    cells = [
      ['Events · range', dpK(t.events), <DPDelta cur={c.events} prev={p?.events} partialToday={today}/>],
      ['Web share', dpPct(t.web, t.events), <span>{dpK(t.web)} / {dpK(t.server)} events</span>],
      ['Person match · purchases', dpRate(t.matchPct), <DPDelta cur={c.matchPct} prev={p?.matchPct} suffix="pp" partialToday={today}/>],
      ['Tickets', dpK(t.tickets), <DPDelta cur={c.tickets} prev={p?.tickets} partialToday={today}/>],
      ['Conversions queued', dpK(t.queued),
        <span className="dp-kpi-parts" title="Every conversion whose event falls in the range, sent or not yet sent">
          <span>by event date</span>
          {p?.convTruncated
            ? convTruncNote
            : <DPDelta cur={c.queued} prev={p ? p.queued : null} partialToday={today}/>}
          {scoped && <GlobalMark/>}
        </span>],
      ['Delivered · by event date', dpK(t.delivered),
        <span className="dp-kpi-parts" title="Delivered conversions whose event falls in the range">
          <span>{dpRate(t.failPct)} failed</span>
          {t.logged > 0 && <span title="log_only: built, never sent">+{dpK(t.logged)} log-only</span>}
          {p?.convTruncated
            ? convTruncNote
            : <DPDelta cur={c.failPct} prev={p?.failPct} invert suffix="pp" partialToday={today}/>}
          {scoped && <GlobalMark/>}
        </span>],
    ];
  }
  return (
    <div className="kpi-strip dp-kpis" {...kpiScrollRegionProps()}>
      {cells.map(([l, v, s]) => (
        <div className="kpi" key={l}>
          <div className="kpi-label">{l}</div>
          <div className="kpi-val"><DPTick>{v}</DPTick></div>
          <div className="kpi-sub">{s}</div>
        </div>
      ))}
    </div>
  );
}

// ─── admin reveal (shared by inspector rows + recent tickets) ────────────────

function RevealButton({ what, onConfirm }: { what: string; onConfirm: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setConfirming(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <span className="dp-confirm-wrap" ref={ref}>
      <button className="dp-reveal-btn" onClick={() => setConfirming((c) => !c)}>{eyeIcon} Reveal PII</button>
      {confirming && (
        <div className="dp-confirm">
          <div className="dp-confirm-arrow"/>
          <h6>Reveal raw PII?</h6>
          <p>Reveal raw PII for this {what}? This access is recorded in the audit log.</p>
          <div className="dp-confirm-actions">
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirming(false)}>Cancel</button>
            <button className="btn btn-sm dp-btn-danger" onClick={() => { setConfirming(false); onConfirm(); }}>
              {eyeIcon} Reveal · logged
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

type RawRow = [string, string | null];

interface RawSection {
  title?: string;
  rows: RawRow[];
}

/**
 * Absolute 60s reveal expiry (§6.4), anchored to the wall-clock `revealedAt`
 * stamped at mutation success. Lives at the OWNER of the reveal state — not
 * in RawPanel — so the window can't be restarted by hiding/remounting the
 * panel (e.g. a filter change that hides the revealed row), and expiry fires
 * even while the panel is unmounted.
 */
function useRevealExpiry(revealedAt: number | null | undefined, clear: () => void) {
  useEffect(() => {
    if (revealedAt == null) return;
    const id = window.setTimeout(clear, revealMsLeft(revealedAt, Date.now()));
    return () => window.clearTimeout(id);
  }, [revealedAt, clear]);
}

// The RAW PII panel — mono, bad-tinted, 60s countdown, audited. The countdown
// is DERIVED from the wall-clock anchor: a remount within the window shows
// the true remaining time, never a fresh 60s.
function RawPanel({ auditId, revealedAt, sections, onClose }: {
  auditId: string;
  revealedAt: number;
  sections: RawSection[];
  onClose: () => void;
}) {
  const nowMs = useNowMs(1000);
  const left = revealSecondsLeft(revealedAt, nowMs);
  return (
    <div className="dp-raw">
      <div className="dp-raw-hd">
        <span>⚠ Raw PII — revealed</span>
        <span style={{ color: 'color-mix(in oklch, var(--bad) 70%, var(--ink-2))' }}>· Logged to audit ({auditId})</span>
        <span className="cnt">Auto-hides in {left}s</span>
        <button className="btn btn-sm btn-ghost" style={{ padding: '0 6px', color: 'var(--bad)' }} onClick={onClose} title="Hide now">
          {Icons.x}
        </button>
      </div>
      <div className="dp-raw-body">
        {sections.map((sect, si) => (
          <Fragment key={si}>
            {sect.title && <div className="dp-raw-sect">{sect.title}</div>}
            {sect.rows.map(([k, v]) => (
              <Fragment key={k}>
                <div className="k">{k}</div>
                <div className={'v' + (v == null ? ' nul' : '')}>{v == null ? '—' : v}</div>
              </Fragment>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// Reveal-404 failure state (§6.4). The explanation is the CALLER's — the
// live-buffer story is true for events only; tickets are durably stored.
function RawGone({ msg }: { msg: string }) {
  return (
    <div className="dp-raw">
      <div className="dp-raw-hd"><span>⚠ Raw PII — unavailable</span></div>
      <div className="dp-raw-gone">{msg}</div>
    </div>
  );
}

function eventGoneMessage(capacity: number, maxAgeSecs: number): string {
  const limits = capacity > 0 && maxAgeSecs > 0
    ? `${capacity.toLocaleString()} events · ${dpDur(maxAgeSecs)}`
    : 'runtime limits not reported by this SAM Server build';
  return `Event has left the live buffer. The buffer is in-memory only (${limits}) — raw PII is never stored.`;
}
const TICKET_GONE_MSG =
  'Ticket not found — the row may have been re-ingested under a different line id.';

// ─── context panel (§5.6) — inline below the diagram, never a drawer ─────────

function StatCell({ l, v, s }: { l: string; v: ReactNode; s?: ReactNode }) {
  return (
    <div className="dp-context-cell">
      <span className="l">{l}</span>
      <span className="v">{v}</span>
      {s != null && <span className="s">{s}</span>}
    </div>
  );
}

/**
 * A sale time for the tickets table: the clock alone when it is today in New
 * York, else the date before it. A row from last week used to read as if it
 * sold this morning.
 */
export function ticketSaleTime(iso: string, nowMs: number): string {
  const clock = dpClock(iso);
  return nyStartOfDayMs(iso) === nyStartOfDayMs(nowMs) ? clock : fmtDateShort(iso) + ' ' + clock.slice(0, 5);
}

// Newest tickets (encore context, §6.5) — redacted lane + audited reveal. The
// rows are the newest SALES in scope, not the tickets a run wrote: a run's
// membership cannot be recovered (`written_at` moves on every upsert, and each
// nightly run rewrites 30 days), so the table never claims to be "the last run".
/** One spelling for a production id: the bare lowercase UUID behind `Production/<uuid>`. */
export function productionKey(id: string): string {
  return id.replace(/^Production\//, '').toLowerCase();
}

/** Rows of Recent tickets a phone shows before "Show all". */
const PHONE_TICKET_ROWS = 10;

function RecentTickets({ productionId, prodLabel, canReveal, nowMs, onTrace }: {
  productionId: string | null;
  prodLabel: string;
  canReveal: boolean;
  nowMs: number;
  onTrace: (id: string) => void;
}) {
  const q = useFlowTicketsRecent(productionId, true);
  const { data: productions } = useProductions();
  // A phone shows the newest ten; the rest are one tap away.
  const phone = usePhone();
  const [showAll, setShowAll] = useState(false);
  // A ticket's production is a bare UUID; a catalog row may be stored under its
  // `Production/<uuid>` spelling (or the reverse). Both name the same row.
  const productionName = useMemo(() => {
    const names = new Map<string, string>();
    for (const p of productions ?? []) {
      names.set(p.id, p.name);
      names.set(productionKey(p.id), p.name);
    }
    return (id: string | null) => (id ? names.get(id) ?? names.get(productionKey(id)) ?? null : null);
  }, [productions]);
  const [revealed, setRevealed] = useState<{ id: string; raw: TicketRevealResponse | null; gone: boolean; revealedAt: number } | null>(null);
  const mut = useRevealTicket();
  const clearRevealed = useCallback(() => setRevealed(null), []);
  useRevealExpiry(revealed?.revealedAt, clearRevealed);

  if (q.isLoading) {
    return <div className="muted" style={{ padding: 14, fontSize: 11.5 }}>Loading recent tickets…</div>;
  }
  if (q.isError) {
    return <QueryErrorNote error={q.error} what="recent tickets" onRetry={() => void q.refetch()}/>;
  }
  const tickets = q.data?.rows ?? [];
  if (!tickets.length) {
    return <div className="dp-empty" style={{ padding: 20 }}><span>No tickets for {prodLabel}</span></div>;
  }

  const confirmReveal = (t: TicketFlowRedacted) => {
    mut.mutate(t.ticket_id, {
      onSuccess: (resp) => { setRevealed({ id: t.ticket_id, raw: resp, gone: false, revealedAt: Date.now() }); mut.reset(); },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 404) {
          setRevealed({ id: t.ticket_id, raw: null, gone: true, revealedAt: Date.now() });
          mut.reset();
        }
      },
    });
  };

  const rawRows = (resp: TicketRevealResponse): RawRow[] => {
    const raw = resp.ticket;
    return [
      ['emails', raw.customer_emails ? raw.customer_emails.join(', ') : null],
      ['phones', raw.customer_phones ? raw.customer_phones.join(', ') : null],
      ['name', raw.first_name ? (raw.first_name + ' ' + (raw.last_name ?? '')).trim() : null],
      ['address', raw.address_1 ? `${raw.address_1}, ${raw.city ?? ''} ${raw.state ?? ''} ${raw.post_code ?? ''}`.trim() : null],
      ['amount', raw.amount != null ? '$' + raw.amount.toFixed(2) : null],
      ['person_id', raw.person_id],
      ['household_id', raw.household_id],
    ];
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span className="filter-lbl">Newest tickets by sale time{productionId ? ' · ' + prodLabel : ''}</span>
        <span className="dp-context-kind">{tickets.length} · redacted</span>
      </div>
      <ScrollTable label="Newest tickets" identityColumn={1}>
      <table className="dp-tickets">
        <thead>
          <tr>
            <th>Ticket</th><th>Sale time · ET</th><th>Production</th><th>Contact · Amount</th><th>Resolution</th>
            {canReveal && <th/>}
          </tr>
        </thead>
        <tbody>
          {(phone && !showAll ? tickets.slice(0, PHONE_TICKET_ROWS) : tickets).map((t) => (
            <Fragment key={t.ticket_id}>
              <tr>
                <td className="mono" style={{ fontWeight: 600 }}>
                  {t.source_ticket_id ? (
                    // The trace keys on the ticket id (what lineage stores); the
                    // line id is this row's own key, used by the reveal.
                    <button type="button" className="dp-trace-link"
                      title={`Trace this ticket's journey · line ${t.ticket_id}`}
                      onClick={() => onTrace('tkt:' + t.source_ticket_id)}>{t.source_ticket_id}</button>
                  ) : (
                    <span title={`Line ${t.ticket_id}: no ticket id on this line, so its journey can't be traced`}>—</span>
                  )}
                </td>
                <td className="mono" style={{ color: 'var(--ink-2)' }}>{ticketSaleTime(t.created_at, nowMs)}</td>
                <td style={{ color: 'var(--ink-2)' }} title={t.production_id ?? undefined}>
                  {productionName(t.production_id) ?? (t.production_id
                    ? <span className="mono">Unknown production ({t.production_id.slice(0, 8)}…)</span>
                    : '—')}
                </td>
                <td><TicketChips t={t}/></td>
                <td>
                  <span style={{ display: 'inline-flex', gap: 10 }}>
                    <span className={'dp-flag ' + (t.person_id_resolved ? 'on' : 'offf')}><span className="d"/>person</span>
                    <span className={'dp-flag ' + (t.household_id_present ? 'on' : 'offf')}><span className="d"/>household</span>
                    {t.anonymous && <span className="dp-flag warn"><span className="d"/>anonymous</span>}
                  </span>
                </td>
                {canReveal && (
                  <td style={{ textAlign: 'right' }}>
                    {revealed?.id !== t.ticket_id && <RevealButton what="ticket" onConfirm={() => confirmReveal(t)}/>}
                  </td>
                )}
              </tr>
              {revealed?.id === t.ticket_id && (
                <tr>
                  <td colSpan={canReveal ? 6 : 5} style={{ padding: '4px 0 8px' }}>
                    {revealed.gone
                      ? <RawGone msg={TICKET_GONE_MSG}/>
                      : revealed.raw && (
                        <RawPanel
                          auditId={revealed.raw.audit_id}
                          revealedAt={revealed.revealedAt}
                          onClose={clearRevealed}
                          sections={[{ rows: rawRows(revealed.raw) }]}
                        />
                      )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      </ScrollTable>
      {phone && !showAll && tickets.length > PHONE_TICKET_ROWS && (
        <button type="button" className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => setShowAll(true)}>
          Show all {tickets.length} tickets
        </button>
      )}
      {mut.isError && !(mut.error instanceof ApiError && mut.error.status === 404) && (
        <div style={{ paddingTop: 8 }}><MutationErrorLine error={mut.error}/></div>
      )}
    </div>
  );
}

function ContextPanel({ selected, snapshot, nowMs, productionId, prodLabel, feedPage, canReveal, canLogs, scoped, onClose, onTrace }: {
  selected: DpNodeId;
  snapshot: PipelineFlowSnapshot;
  nowMs: number;
  productionId: string | null;
  prodLabel: string;
  /** The 5 s feed page: seq and contention counts are read from it, not the 60 s snapshot. */
  feedPage: FlowLivePage | undefined;
  canReveal: boolean;
  canLogs: boolean;
  scoped: boolean;
  onClose: () => void;
  onTrace: (id: string) => void;
}) {
  const node = DP_NODES.find((n) => n.id === selected)!;
  const group = DP_NODE_GROUP[selected];
  const d = useMemo(() => dpDerive(snapshot, nowMs).nodes[selected], [snapshot, nowMs, selected]);
  const b = snapshot.batch;
  const rows = (src: string) => b.web_events.filter((r) => r.source === src);
  const sum = (rws: typeof b.web_events, k: 'count_5m' | 'count_1h' | 'count_24h') =>
    rws.reduce((s, r) => s + num(r[k]), 0);
  const conv = b.conversions;
  const logOnly = conv.mode === 'log_only';
  const run = snapshot.last_ingest_run;
  const liveRun = snapshot.running_ingest_run ?? null;
  const rz = b.resolve_1h;

  let cells: ReactNode = null;
  let body: ReactNode = null;
  let link: ReactNode = null;

  if (group === 'sources') {
    const src = selected === 'sgtm' ? 'pixel_sgtm' : selected === 'web-pixel' ? 'pixel' : null;
    const rws = src ? rows(src) : b.web_events;
    const byType = new Map<string, number>();
    for (const r of rws) byType.set(r.event_type, (byType.get(r.event_type) ?? 0) + num(r.count_24h));
    const top = [...byType.entries()].sort((a, z) => z[1] - a[1]);
    const lastIso = rws.map((r) => r.last_at).filter((t): t is string => !!t).sort().pop() ?? null;
    const seq = feedPage ? num(feedPage.latest_seq) : num(b.tap.latest_seq);
    cells = (
      <>
        <StatCell l="Rate · 5m" v={dpN(sum(rws, 'count_5m') / 5) + '/min'} s={dpN(sum(rws, 'count_5m')) + ' events'}/>
        <StatCell l="Events · 1h" v={dpN(sum(rws, 'count_1h'))} s={'last ' + dpAge(lastIso, nowMs)}/>
        <StatCell l="Events · 24h" v={dpN(sum(rws, 'count_24h'))} s={top.map(([t, n]) => t + ' ' + dpK(n)).join(' · ')}/>
        <StatCell l="Live tap" v={b.tap.enabled ? 'ON' : 'OFF'}
          s={`seq ${dpN(seq)}${num(b.tap.capacity) > 0 ? ` · capacity ${num(b.tap.capacity)} · TTL ${dpDur(num(b.tap.max_age_secs))}` : ' · limits unavailable'}${feedPage ? ' · since ' + dpClock(feedPage.started_at).slice(0, 5) : ''}`}/>
      </>
    );
    link = <Link to="/backend/pixels" className="dp-deep">Website Pixels →</Link>;
  } else if (group === 'store') {
    const lost = num(feedPage ? feedPage.dropped_captures : b.tap.dropped_captures);
    const purchases = num(rz.purchases_1h);
    const matched = num(rz.purchase_person_1h);
    cells = (
      <>
        <StatCell l="Ingest rate" v={dpN(sum(b.web_events, 'count_5m') / 5) + '/min'} s={dpN(sum(b.web_events, 'count_1h')) + ' events 1h'}/>
        <StatCell l="Person match · purchases" v={dpPct(matched, purchases, 0)}
          s={dpN(matched) + ' of ' + dpN(purchases) + ' purchases · 1h'}/>
        <StatCell l="User hash · 1h" v={dpPct(num(rz.user_id_1h), num(rz.events_1h), 0)} s={dpN(num(rz.user_id_1h)) + ' of ' + dpN(num(rz.events_1h)) + ' events with PII flowing'}/>
        <StatCell l="Captures lost to contention" v={dpN(lost)}
          s={<span style={lost > 0 ? { color: 'var(--warn)' } : undefined}>since SAM Server start</span>}/>
      </>
    );
    link = canLogs ? <Link to="/admin/logs" className="dp-deep">Logs (admins) →</Link> : null;
  } else if (group === 'encore') {
    const fin = run?.finished_at != null ? dpAge(new Date(num(run.finished_at)).toISOString(), nowMs) : '—';
    cells = (
      <>
        <StatCell l="Last run · tickets" v={run && run.tickets_out != null ? dpN(num(run.tickets_out)) : '—'}
          s={run ? <>in {dpN(num(run.tickets_in))} · new {dpN(num(run.tickets_new))} · upd {dpN(num(run.tickets_updated))}</> : 'no finished run yet'}/>
        {/* `resolved_ct` counts the tickets SENT to the resolver, not matches:
            the match rate is the History "Person match" ticket figure. */}
        <StatCell l="Sent for resolution" v={run && run.tickets_out != null ? dpN(num(run.resolved_ct)) + ' of ' + dpN(num(run.tickets_out)) : '—'}
          s={run ? <>carried {dpN(num(run.carried_ct))} · anonymous {dpN(num(run.anonymous_ct))}</> : '—'}/>
        <StatCell l="Source lag" v={(run?.source_lag_hours != null ? num(run.source_lag_hours) : '—') + 'h'}
          s={run ? run.trigger + ' · ' + run.status + ' · ' + fin : '—'}/>
        <StatCell l="In flight" v={liveRun ? runningLabel(liveRun) : '—'}
          s={!liveRun ? 'no run in flight'
            : liveRun.trigger + (liveRun.heartbeat_at != null
              ? ` · updated ${dpAge(new Date(num(liveRun.heartbeat_at)).toISOString(), nowMs)}`
              : ' · not started yet') + (liveRun.progress_note ? ' · ' + liveRun.progress_note : '')}/>
      </>
    );
    body = (
      <>
        <div className="mono" style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', color: 'var(--ink-3)', fontSize: 10 }}>
          {cronWords(b.encorepro.schedule?.cron, b.encorepro.schedule?.timezone)}
        </div>
        <RecentTickets productionId={productionId} prodLabel={prodLabel} canReveal={canReveal} nowMs={nowMs} onTrace={onTrace}/>
      </>
    );
    link = <Link to="/backend/pipeline" className="dp-deep">EncorePro Pipeline →</Link>;
  } else {
    // log_only: throughput is LOGGED rows; failures are their own count.
    const m = targetCounts(conv.by_target.find((t) => t.target === 'meta'), logOnly);
    const t = targetCounts(conv.by_target.find((x) => x.target === 'ttd'), logOnly);
    const word = logOnly ? ' logged' : ' sent';
    const bySource = conv.by_source_target ?? [];
    const fromSource = (source: string, k: 'enqueued_1h' | 'delivered_24h' | 'logged_24h') =>
      bySource.filter((x) => x.source === source).reduce((sum0, x) => sum0 + num(x[k]), 0);
    const outKey = logOnly ? 'logged_24h' : 'delivered_24h';
    cells = (
      <>
        <StatCell l="Queue depth" v={dpN(num(conv.queue_depth))}
          s={'oldest pending ' + dpDur(conv.oldest_pending_age_secs == null ? null : num(conv.oldest_pending_age_secs))
            + (bySource.length
              ? ' · enqueued 1h: web ' + dpN(fromSource('web_event', 'enqueued_1h')) + ' · tickets ' + dpN(fromSource('ticket', 'enqueued_1h'))
              : '')}/>
        <StatCell l="Meta · 1h" v={dpN(m.delivered_1h) + word}
          s={<>{conv.meta_enabled ? (conv.meta_circuit_open ? 'CIRCUIT OPEN' : 'circuit closed') : 'not configured'} · {dpN(m.failed_1h)} failed · {dpN(m.pending)} pending</>}/>
        <StatCell l="Trade Desk · 1h" v={dpN(t.delivered_1h) + word}
          s={<>{conv.ttd_enabled ? (conv.ttd_circuit_open ? 'CIRCUIT OPEN' : 'circuit closed') : 'not configured'} · {dpN(t.failed_1h)} failed · {dpN(t.pending)} pending</>}/>
        <StatCell l={logOnly ? 'Logged · 24h (not sent)' : 'Sent · 24h'} v={dpN(m.delivered_24h + t.delivered_24h)}
          s={dpN(m.failed_24h + t.failed_24h) + ' failed · by time sent'
            + (bySource.length
              ? ' · web ' + dpN(fromSource('web_event', outKey)) + ' · tickets ' + dpN(fromSource('ticket', outKey))
              : '')}/>
      </>
    );
    link = <Link to="/backend/capi-forwarding" className="dp-deep">CAPI Forwarding →</Link>;
  }

  const globalGroup = group === 'encore' || group === 'activation';

  return (
    <section className="panel dp-context" aria-label={'Current status (live) · ' + node.name}>
      <div className="panel-hd">
        <div className="dp-context-title">
          <span className="d" style={{ background: dpHealthColor(d.health) }}/>
          <h3>{node.name}</h3>
          <span className="dp-context-kind">{node.kind}</span>
          <span className="dp-context-kind" title="These figures are the live snapshot, whatever the page's mode or range"
            style={{ color: 'var(--ink-2)' }}>· Current status (live)</span>
          {group === 'activation' && <ModeBadge mode={conv.mode} enabled={conv.enabled}/>}
          {scoped && globalGroup && (
            <span className="dp-context-kind" title="Ticket + conversion stats can't be scoped to a production — showing global numbers"
              style={{ color: 'var(--ink-3)' }}>· GLOBAL</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {link}
          <button className="btn btn-sm btn-ghost" onClick={onClose} title="Close (Esc)">{Icons.x}</button>
        </div>
      </div>
      <div className="dp-context-grid">{cells}</div>
      {body && <div className="dp-context-body">{body}</div>}
    </section>
  );
}

// ─── payload inspector (§6) — LIVE mode only ─────────────────────────────────

const FcFlag = ({ fc, onLbl, offLbl }: { fc: FieldCheck; onLbl: string; offLbl: string }) => (
  <span className={'dp-flag ' + (fc === 'valid' ? 'on' : fc === 'invalid' ? 'warn' : 'offf')}>
    <span className="d"/>{fc === 'absent' ? offLbl : onLbl}
  </span>
);

interface RevealState {
  seq: number;
  raw: FlowRevealResponse | null;
  gone: boolean;
  /** Wall-clock anchor (ms) stamped at mutation success — the 60s TTL is absolute. */
  revealedAt: number;
}

// Expanded row: redacted detail (§6.3) + admin reveal (§6.4).
function ExpandedRow({ ev, canReveal, reveal, revealError, bufferCapacity, bufferMaxAgeSecs, onReveal, onCloseReveal, onTrace }: {
  ev: FlowEventRedacted;
  canReveal: boolean;
  reveal: RevealState | null;
  revealError: unknown;
  bufferCapacity: number;
  bufferMaxAgeSecs: number;
  onReveal: () => void;
  onCloseReveal: () => void;
  onTrace: (id: string) => void;
}) {
  const raw = reveal && !reveal.gone ? reveal.raw : null;
  const rawSections: RawSection[] = raw
    ? ([
        raw.event.raw_pii ? { title: 'Raw PII', rows: Object.entries(raw.event.raw_pii) as RawRow[] } : null,
        raw.event.hashed_pii
          ? { title: 'Hashed PII (sha256)', rows: (Object.entries(raw.event.hashed_pii) as RawRow[]).map(([k, v]) => [k, v == null ? null : v.slice(0, 28) + '…'] as RawRow) }
          : null,
        raw.event.identity ? { title: 'Identity values', rows: Object.entries(raw.event.identity) as RawRow[] } : null,
        {
          title: 'Context',
          rows: [
            ['page_url', raw.event.page_url],
            ['dataLayer', raw.event.vars_json],
            ['person_id', raw.event.person_id],
            ['user_id', raw.event.user_id],
          ] as RawRow[],
        },
      ].filter(Boolean) as RawSection[])
    : [];

  return (
    <div className="dp-expand-inner">
      <div className="dp-expand-grid">
        <div className="dp-x-sect">
          <h5>Page context</h5>
          <div className="dp-x-row"><span className="k">domain</span><span className="v">{ev.domain}</span></div>
          <div className="dp-x-row"><span className="k">page_url</span><span className="v"><FcFlag fc={ev.page_url} onLbl="present — redacted" offLbl="absent"/></span></div>
          <div className="dp-x-row"><span className="k">country · edge location</span><span className="v">{ev.country} · {ev.colo}</span></div>
          <div className="dp-x-row"><span className="k">browser</span><span className="v">{ev.ua_family}</span></div>
          <div className="dp-x-row"><span className="k">consent</span><span className="v"><ConsentFlags consent={ev.consent}/></span></div>
        </div>
        <div className="dp-x-sect">
          <h5>Event ids</h5>
          <div className="dp-x-row"><span className="k">web_event_id</span><span className="v" title={ev.web_event_id}>{ev.web_event_id.slice(0, 13)}…</span></div>
          <div className="dp-x-row"><span className="k">event_at</span><span className="v">{fmtStampEt(ev.event_at)}</span></div>
          <div className="dp-x-row"><span className="k">captured_at</span><span className="v">{fmtStampEt(ev.captured_at)}</span></div>
          <div className="dp-x-row"><span className="k">buffer seq</span><span className="v">{String(Number(ev.seq))}</span></div>
          <div className="dp-x-row"><span className="k">tenant</span><span className="v">{ev.tenant_id}</span></div>
          <div className="dp-x-row" style={{ marginTop: 4 }}>
            <span className="k">lineage</span>
            <span className="v" style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="dp-trace-link" onClick={() => onTrace('web:' + ev.web_event_id)}>
                Trace event →
              </button>
              {ev.ticket_id && (
                <button type="button" className="dp-trace-link" onClick={() => onTrace('tkt:' + ev.ticket_id)}>
                  Trace ticket →
                </button>
              )}
            </span>
          </div>
        </div>
        <div className="dp-x-sect">
          <h5>Business</h5>
          <div className="dp-x-row"><span className="k">production</span><span className="v">{ev.production_id ?? <span className="dim">—</span>}</span></div>
          <div className="dp-x-row"><span className="k">source ref</span><span className="v">{ev.source_reference ?? <span className="dim">—</span>}</span></div>
          <div className="dp-x-row"><span className="k">venue</span><span className="v">{ev.venue_id ?? <span className="dim">—</span>}</span></div>
          <div className="dp-x-row"><span className="k">order</span><span className="v">{ev.ticket_id ?? <span className="dim">—</span>}</span></div>
          <div className="dp-x-row"><span className="k">value</span><span className="v">
            {ev.value != null
              ? '$' + ev.value.toFixed(2) + ' ' + (ev.currency ?? '⚠ no currency') + (ev.units ? ' · ' + ev.units + ' tickets' : '')
              : <span className="dim">—</span>}
          </span></div>
        </div>
        <div className="dp-x-sect">
          <h5>dataLayer snapshot</h5>
          <div className="dp-x-row"><span className="k">vars</span><span className="v"><FcFlag fc={ev.vars} onLbl="present — keys only" offLbl="absent"/></span></div>
          <div className="dp-x-row"><span className="k">size</span><span className="v">{ev.vars_bytes != null ? dpN(num(ev.vars_bytes)) + ' bytes' : <span className="dim">—</span>}</span></div>
          <div className="dp-x-row"><span className="k">person</span><span className="v"><MatchDot resolved={ev.person_id_resolved}/>{ev.person_id_resolved === true ? ' resolved' : ev.person_id_resolved === null ? ' pending' : ' no match'}</span></div>
          <div className="dp-x-row"><span className="k">user hash</span><span className="v">{ev.user_id_present ? 'present — redacted' : <span className="dim">absent</span>}</span></div>
        </div>
      </div>

      <div className="dp-x-sect">
        <h5>
          Conversion · targets {ev.conversion_targets.length ? '[' + ev.conversion_targets.join(', ') + ']' : '[]'}
          {' '}· signal mask {ev.conversion_signals ? '`' + ev.conversion_signals + '`' : '—'}
        </h5>
        <MaskChips mask={ev.conversion_signals}/>
      </div>

      {canReveal && !reveal && (
        <div>
          <RevealButton what="event" onConfirm={onReveal}/>
          {revealError != null && !(revealError instanceof ApiError && revealError.status === 404) && (
            <div style={{ marginTop: 8 }}><MutationErrorLine error={revealError}/></div>
          )}
        </div>
      )}
      {canReveal && reveal && (reveal.gone
        ? <RawGone msg={eventGoneMessage(bufferCapacity, bufferMaxAgeSecs)}/>
        : raw && <RawPanel auditId={raw.audit_id} revealedAt={reveal.revealedAt} onClose={onCloseReveal} sections={rawSections}/>)}
    </div>
  );
}

interface FeedApi {
  page: FlowLivePage | undefined;
  events: FlowEventRedacted[];
  allEvents: FlowEventRedacted[];
  newSeqs: ReadonlySet<number>;
  isError: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => unknown;
}

function Inspector({
  snapshot, feed, paused, pausedAt, onPause, onResume,
  scoped, prodLabel, onClearProd,
  chan, setChan, etype, setEtype, pixel, setPixel,
  canReveal, tapEnabled, onTrace,
}: {
  snapshot: PipelineFlowSnapshot;
  feed: FeedApi;
  paused: boolean;
  pausedAt: string | null;
  onPause: () => void;
  onResume: () => void;
  scoped: boolean;
  prodLabel: string;
  onClearProd: () => void;
  chan: string;
  setChan: (v: string) => void;
  etype: string;
  setEtype: (v: string) => void;
  pixel: string;
  setPixel: (v: string) => void;
  canReveal: boolean;
  tapEnabled: boolean;
  onTrace: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [reveal, setReveal] = useState<RevealState | null>(null);
  const revealMut = useRevealFlowEvent();
  const clearReveal = useCallback(() => setReveal(null), []);

  // Resume closes any open reveal (§6.4); route-leave unmounts the state.
  useEffect(() => { if (!paused) setReveal(null); }, [paused]);

  // Absolute 60s expiry — fires even while the revealed row is filtered out
  // or collapsed, so hide/re-show can't keep the payload alive past the TTL.
  useRevealExpiry(reveal?.revealedAt, clearReveal);

  const toggleExpand = (seq: number) => {
    setExpanded((x) => {
      const next = new Set(x);
      if (next.has(seq)) {
        next.delete(seq);
        setReveal((r) => (r && r.seq === seq ? null : r)); // collapse closes reveal
      } else {
        next.add(seq);
      }
      return next;
    });
  };

  const confirmReveal = (ev: FlowEventRedacted) => {
    onPause(); // auto-pause FIRST so the row can't scroll away
    const seq = Number(ev.seq);
    revealMut.mutate(seq, {
      onSuccess: (resp) => {
        // LOCAL state only — the raw payload never enters the query cache.
        setReveal({ seq, raw: resp, gone: false, revealedAt: Date.now() });
        revealMut.reset();
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 404) {
          setReveal({ seq, raw: null, gone: true, revealedAt: Date.now() }); // event left the buffer
          revealMut.reset();
        }
      },
    });
  };

  const tap = snapshot.batch.tap;
  // Seq and contention come from the 5 s feed page; the 60 s snapshot is only
  // the fallback before the first page lands. (Taking the max of the two kept
  // showing a pre-restart seq for a minute after a buffer restart.) Capacity
  // and TTL are configuration, so the snapshot's copy is current.
  const lost = num(feed.page ? feed.page.dropped_captures : tap.dropped_captures);
  const pixels = [...new Set(feed.allEvents.map((e) => e.pixel_id))].sort();
  const shown = feed.events;
  const seqShown = num(feed.page ? feed.page.latest_seq : tap.latest_seq);
  const feedActive = !paused && tapEnabled && !feed.isError && !!feed.page && feed.allEvents.length > 0;

  return (
    <section className="panel dp-inspector">
      <div className="panel-hd">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span className={'dp-live-dot' + (feedActive ? '' : ' paused')}/>
          <h3 style={{ whiteSpace: 'nowrap' }}>Payload Inspector</h3>
          <span className="dp-context-kind" style={{ marginLeft: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Redacted · presence only — values never leave SAM Server
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 12 }}>
          {lost > 0 && (
            <span className="dp-warn-chip" title="Captures lost to contention · since SAM Server start">
              ⚠ {dpN(lost)} lost to contention
            </span>
          )}
          <span className="dp-context-kind">
            {num(tap.capacity) > 0 ? `buffer ${num(tap.capacity)} · TTL ${dpDur(num(tap.max_age_secs))}` : 'limits unavailable'} · seq {dpN(seqShown)}
          </span>
          {tapEnabled && (paused
            ? <button className="btn btn-sm" onClick={onResume}>{Icons.play} Resume</button>
            : <button className="btn btn-sm" onClick={onPause}>{Icons.pause} Pause</button>)}
        </div>
      </div>

      {paused && (
        <div className="dp-paused-strip">
          <span>Paused · feed frozen at {pausedAt ? withEt(dpClock(pausedAt)) : '—'}</span>
          <span style={{ opacity: 0.7 }}>reveals stay put while frozen</span>
        </div>
      )}

      {feed.isError && (
        <QueryErrorNote error={feed.error} what="the live payload feed" onRetry={() => void feed.refetch()}/>
      )}

      <div className="dp-insp-filters">
        {scoped && (
          <button className="chip on" onClick={onClearProd} title="Scoped from the page header — click to clear">
            Production · {prodLabel}<span className="x" style={{ marginLeft: 4 }}>×</span>
          </button>
        )}
        <span className="filter-lbl">Channel</span>
        {([['all', 'All'], ['web', 'Web'], ['server', 'Server']] as const).map(([id, l]) => (
          <button key={id} className={'chip' + (chan === id ? ' on' : '')} onClick={() => setChan(id)}>{l}</button>
        ))}
        <span className="filter-lbl" style={{ marginLeft: 10 }}>Event</span>
        {([['all', 'All'], ['pageview', 'Pageview'], ['purchase', 'Purchase']] as const).map(([id, l]) => (
          <button key={id} className={'chip' + (etype === id ? ' on' : '')} onClick={() => setEtype(id)}>{l}</button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <Dropdown label="PIXEL" value={pixel} onChange={setPixel} rightAlign
            options={[{ id: 'all', label: 'All pixels' }, ...pixels.map((p) => ({ id: p, label: p }))]}/>
        </div>
      </div>

      {!tapEnabled ? (
        <div className="dp-empty">
          <span>Live capture off</span>
          <span style={{ letterSpacing: '.06em', textTransform: 'none', fontSize: 10, color: 'var(--ink-3)' }}>
            The payload tap is off in the SAM Server config — historical charts remain available.
          </span>
        </div>
      ) : feed.isError ? null : feed.allEvents.length === 0 ? (
        <div className="dp-empty">
          <span>Collecting{feed.page ? ' · buffer started ' + withEt(dpClock(feed.page.started_at)) : '…'}</span>
          <span>Events appear as they arrive</span>
        </div>
      ) : (
        <div style={{ maxHeight: 580, overflow: 'auto' }}>
          <table className="dp-feed">
            <thead>
              <tr>
                <th style={{ width: 76 }}>Time · ET</th>
                <th style={{ width: 74 }}>Channel</th>
                <th style={{ width: 86 }}>Pixel</th>
                <th>Event</th>
                <th style={{ width: 110 }} title="E P F L A — email, phone, first, last, address bundle">PII</th>
                <th style={{ width: 74 }} title="V C O — value, currency, order id">Price</th>
                <th style={{ width: 200 }} title="e f c F g G s t T i — identity envelope, mask order">Identity</th>
                <th style={{ width: 56 }}>Match</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((ev) => {
                const seq = Number(ev.seq);
                const isOpen = expanded.has(seq);
                return (
                  <Fragment key={seq}>
                    <tr className={'dp-row' + (isOpen ? ' open' : '') + (feed.newSeqs.has(seq) ? ' dp-new' : '')}
                        onClick={() => toggleExpand(seq)}>
                      <td className="t">{dpClock(ev.captured_at)}</td>
                      <td>
                        <span className={'dp-ch ' + (ev.channel === 'pixel' ? 'web' : 'server')}>
                          <span className="gl"/>{ev.channel === 'pixel' ? 'WEB' : 'SERVER'}
                        </span>
                      </td>
                      <td className="px">{ev.pixel_id}</td>
                      <td>
                        <span className={'ev' + (isEventType(ev.event_type, 'pageview') ? ' pv' : '')}>{ev.event_type}</span>
                        {ev.value != null && (
                          <span className="tnum" style={{ marginLeft: 8, fontSize: 10.5, color: 'var(--ink-1)' }}>
                            ${ev.value.toFixed(2)}{ev.units != null && ev.units > 1 ? ' ×' + ev.units : ''}
                          </span>
                        )}
                        {!ev.consent.ads && <span className="tag warn" style={{ marginLeft: 8, fontSize: 8.5 }}>NO ADS CONSENT</span>}
                      </td>
                      <td><PiiChips ev={ev}/></td>
                      <td><PriceChips ev={ev}/></td>
                      <td><IdentityChips ev={ev}/></td>
                      <td><MatchDot resolved={ev.person_id_resolved}/></td>
                    </tr>
                    {isOpen && (
                      <tr className="dp-expand">
                        <td colSpan={8}>
                          <ExpandedRow
                            ev={ev}
                            canReveal={canReveal}
                            reveal={reveal && reveal.seq === seq ? reveal : null}
                            // The mutation is shared across rows — surface its
                            // error only under the row the reveal belonged to
                            // (`variables` holds that seq while pending/failed).
                            revealError={revealMut.variables === seq ? revealMut.error : null}
                            bufferCapacity={num(tap.capacity)}
                            bufferMaxAgeSecs={num(tap.max_age_secs)}
                            onReveal={() => confirmReveal(ev)}
                            onCloseReveal={clearReveal}
                            onTrace={onTrace}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {shown.length === 0 && (
            <div className="dp-empty">
              <span>No events match these filters</span>
              {scoped && chan === 'all' && etype === 'all' && (
                <span style={{ textTransform: 'none', letterSpacing: '.04em', fontSize: 10, color: 'var(--ink-3)' }}>
                  {prodLabel} has no live web-pixel traffic — coverage gap, not an outage.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ─── HISTORY mode (§7): derive series/totals from the real history responses ─

export interface HistData {
  web: WebEventsHistoryResponse;
  tickets: TicketsHistoryResponse;
  conv: ConversionsHistoryResponse;
}

export interface HistTotals {
  events: number;
  web: number;
  server: number;
  /** Purchase events: the denominator of every purchase-based rate. */
  purchases: number;
  /** Person matched among purchases; `null` when the range holds none. */
  matchPct: number | null;
  userPct: number | null;
  tickets: number;
  ticketMatchPct: number | null;
  delivered: number;
  failed: number;
  /** failed / (delivered + failed); `null` when nothing was sent. */
  failPct: number | null;
  metaDelivered: number;
  ttdDelivered: number;
  metaFailed: number;
  ttdFailed: number;
  /** log_only rows: built and logged, never sent. Not delivered. */
  logged: number;
  /** Every queued conversion in the range, whatever its status, by event date. */
  queued: number;
  /** Queued conversions out of the web lane (`source = web_event`). */
  queuedWeb: number;
  /** Queued conversions out of EncorePro tickets (`source = ticket`). */
  queuedTicket: number;
}

export interface PriorTotals {
  events: number;
  web: number;
  matchPct: number | null;
  tickets: number;
  delivered: number;
  failed: number;
  failPct: number | null;
  queued: number;
  /**
   * The conversions history floor falls INSIDE the compared windows — the
   * prior window's counts are short (its days were partly or wholly deleted),
   * so conversions deltas would show false growth and must be suppressed.
   */
  convTruncated: boolean;
}

export interface HistDerived {
  bucket: HistoryBucket;
  /** Full enumerated NY axis for the requested range (§7) — includes empty buckets. */
  buckets: string[];
  /** False when no lane returned any rows — drives the range-empty state. */
  hasData: boolean;
  data: HistData;
  /** Everything in the range, today included: the figures shown. */
  totals: HistTotals;
  /** COMPLETED days only (before today's New York midnight): what deltas compare. */
  comparable: HistTotals;
  /** The range reaches into today, which `comparable` leaves out. */
  includesToday: boolean;
  rangeLabel: string;
  floor: string | null;
  /** Each bucket against the conversions history floor. */
  floorStates: FloorState[];
  spanDays: number;
  /** Rollup days with stats in the range (UTC days). */
  statsDays: number;
  /** A lane's row cap cut the response: its totals are short. */
  truncated: { web: boolean; tickets: boolean };
  historyTotals: Record<string, string>;
  /** Node tooltips in HISTORY: the range's figures, never the live snapshot's. */
  historyTips: Partial<Record<DpNodeId, Record<string, string>>>;
}

// Delivery statuses roll up for the charts: delivered means a provider received
// it; failed+quarantined both mean it broke. `logged` (log_only dark launches)
// was built and never sent, so it is counted apart, never as delivered: until
// 2026-10-11 it was, and a dark launch's rows kept reading as deliveries for
// the whole retention window after a switch to live. skipped/pending are in
// the queued total only.
const isDeliveredStatus = (s: string) => s === 'delivered';
const isFailedStatus = (s: string) => s === 'failed' || s === 'quarantined';

/**
 * Buckets that end at or before the conversions history floor: no row in
 * them survives. The bucket that contains the floor is PARTIAL (see
 * `bucketFloorStates`), drawn as such, never as empty.
 */
export function bucketsBeforeFloor(
  buckets: string[],
  floorMs: number | null,
  bucket: HistoryBucket,
): boolean[] {
  return bucketFloorStates(buckets, floorMs, bucket).map((s) => s === 'before');
}

function sumWhere<T>(rows: readonly T[], pred: (r: T) => boolean, val: (r: T) => number): number {
  return rows.reduce((s, r) => (pred(r) ? s + val(r) : s), 0);
}

function fmtRangeDate(d: Date): string {
  return fmtDateShort(d);
}

/** Totals over the rows whose bucket passes `keep` (every row by default). */
export function histTotals(data: HistData, keep: (bucketMs: number) => boolean = () => true): HistTotals {
  const inRange = (r: { bucket: string }) => keep(Date.parse(r.bucket));
  const W = data.web.rows.filter(inRange);
  const T = data.tickets.rows.filter(inRange);
  const C = data.conv.rows.filter(inRange);
  const webTotal = sumWhere(W, (r) => r.source === 'pixel', (r) => num(r.total));
  const srvTotal = sumWhere(W, (r) => r.source === 'pixel_sgtm', (r) => num(r.total));
  // Case-insensitive: stored history rows say "Purchase", the live tap "purchase".
  const purchTotal = sumWhere(W, isPurchase, (r) => num(r.total));
  const ticketsTotal = sumWhere(T, () => true, (r) => num(r.total));
  const delivered = sumWhere(C, (r) => isDeliveredStatus(r.status), (r) => num(r.n));
  const failed = sumWhere(C, (r) => isFailedStatus(r.status), (r) => num(r.n));
  const logged = sumWhere(C, (r) => r.status === 'logged', (r) => num(r.n));
  const queued = sumWhere(C, () => true, (r) => num(r.n));
  const of = (target: string, pred: (s: string) => boolean) =>
    sumWhere(C, (r) => r.target === target && pred(r.status), (r) => num(r.n));
  return {
    events: webTotal + srvTotal,
    web: webTotal,
    server: srvTotal,
    purchases: purchTotal,
    matchPct: ratePct(sumWhere(W, isPurchase, (r) => num(r.person_ct)), purchTotal),
    userPct: ratePct(sumWhere(W, isPurchase, (r) => num(r.user_ct)), purchTotal),
    tickets: ticketsTotal,
    ticketMatchPct: ratePct(sumWhere(T, () => true, (r) => num(r.person_ct)), ticketsTotal),
    delivered,
    failed,
    failPct: ratePct(failed, delivered + failed),
    metaDelivered: of('meta', isDeliveredStatus),
    ttdDelivered: of('ttd', isDeliveredStatus),
    metaFailed: of('meta', isFailedStatus),
    ttdFailed: of('ttd', isFailedStatus),
    logged,
    queued,
    queuedWeb: sumWhere(C, (r) => r.source === 'web_event', (r) => num(r.n)),
    queuedTicket: sumWhere(C, (r) => r.source === 'ticket', (r) => num(r.n)),
  };
}

/** The exclusive end of a range: the New York midnight after its last day. */
export function rangeEndExclusiveMs(end: Date): number {
  return nyEndOfDayMsExclusive(end);
}

/** `ms` moved by `days` New York CALENDAR days (23 h / 25 h across DST). */
export function nyAddDays(ms: number, days: number): number {
  const p = nyParts(ms);
  return nyMidnightMs(p.y, p.m, p.d + days);
}

/** Where the comparable (completed-day) part of a range ends: today's NY midnight at the latest. */
function completedCutoffMs(range: { end: Date }, nowMs: number): number {
  return Math.min(rangeEndExclusiveMs(range.end), nyStartOfDayMs(nowMs));
}

/**
 * The New York day the loaded history describes: that of the OLDEST lane's
 * server `as_of`, when it was computed. Not the receive time: the API caches a
 * recent range for 60 s, so a read just after midnight can be served the body
 * computed just before it. Before any data, the wall clock's day.
 */
export function historyReadDayMs(data: HistData | undefined, nowMs: number): number {
  if (!data) return nyStartOfDayMs(nowMs);
  return nyStartOfDayMs(Math.min(Date.parse(data.web.as_of), Date.parse(data.tickets.as_of), Date.parse(data.conv.as_of)));
}

/** Has New York's day moved past the day the loaded history describes? */
export function historyNeedsRollover(readDayMs: number, nowMs: number): boolean {
  return readDayMs < nyStartOfDayMs(nowMs);
}

/**
 * The prior window a range's deltas compare with: as many New York calendar
 * days as the range has COMPLETED days, ending where the range begins. A
 * range ending today leaves today out of the comparison; a range that is only
 * today has nothing to compare (`null`). Days are calendar days, never
 * `start − 24 h` arithmetic, so a DST day keeps its own length.
 */
export function priorWindow(
  range: { start: Date; end: Date },
  nowMs: number,
): { from: string; to: string; days: number } | null {
  const startMs = nyStartOfDayMs(range.start);
  const days = enumerateBuckets(
    new Date(startMs).toISOString(), new Date(completedCutoffMs(range, nowMs)).toISOString(), 'day',
  ).length;
  if (days === 0) return null;
  return { from: new Date(nyAddDays(startMs, -days)).toISOString(), to: new Date(startMs).toISOString(), days };
}

export function deriveHistory(data: HistData, range: { start: Date; end: Date }, nowMs: number): HistDerived {
  const fromIso = nyStartOfDayIso(range.start);
  const toIso = new Date(rangeEndExclusiveMs(range.end)).toISOString();
  const spanDays = enumerateBuckets(fromIso, toIso, 'day').length;
  const bucket = data.web.bucket;
  // Full axis from the REQUESTED range (same bounds the queries used) — an
  // axis built from returned row keys compresses time when buckets are empty
  // and inflates range averages (which divide by the bucket count).
  const buckets = enumerateBuckets(fromIso, toIso, bucket);
  const hasData = data.web.rows.length > 0 || data.tickets.rows.length > 0 || data.conv.rows.length > 0;
  const totals = histTotals(data);
  const cutoff = completedCutoffMs(range, nowMs);
  const comparable = histTotals(data, (ms) => ms < cutoff);
  const includesToday = rangeEndExclusiveMs(range.end) > nyStartOfDayMs(nowMs);
  const floor = data.conv.history_floor;
  const floorStates = bucketFloorStates(buckets, floor != null ? Date.parse(floor) : null, bucket);
  const statsDays = num(data.tickets.stats_days);
  const rangeLabel = (fmtRangeDate(range.start) + ' – ' + fmtRangeDate(range.end)).toUpperCase();
  const pct = (v: number | null) => (v == null ? '—' : Math.round(v) + '%');
  const truncated = { web: data.web.truncated === true, tickets: data.tickets.truncated === true };
  const short = (lane: boolean) => (lane ? ' (short: row cap)' : '');
  // The history contract has no run count — e7 / ingest-jobs show a dash
  // rather than a fabricated total (deviation from the mock's fixture math).
  const historyTotals: Record<string, string> = {
    e1: dpK(totals.web) + ' events',
    e2: dpK(totals.server) + ' events',
    e3: dpK(totals.events),
    e4: dpK(totals.events),
    e5: pct(totals.matchPct) + ' match',
    e6: dpK(totals.queuedWeb) + ' queued',
    e7: '—',
    e8: dpK(totals.tickets) + ' tickets',
    e9: pct(totals.ticketMatchPct) + ' match',
    e11: dpK(totals.metaDelivered) + ' delivered',
    e12: dpK(totals.ttdDelivered) + ' delivered',
    e13: dpK(totals.queuedTicket) + ' queued',
    'n-web-pixel': dpK(totals.web) + ' events',
    'n-sgtm': dpK(totals.server) + ' events',
    'n-pixel-edge': dpK(totals.events) + ' events',
    'n-batch-ingest': dpK(totals.events) + ' processed',
    'n-store-web-events': dpK(totals.events) + ' rows',
    'n-enrich': pct(totals.matchPct) + ' match · purchases',
    'n-encore-pg': dpK(totals.tickets) + ' tickets created',
    'n-ingest-jobs': '—',
    'n-store-tickets': dpK(totals.tickets) + ' tickets',
    'n-d1-stats': dpN(statsDays) + ' of ' + dpN(spanDays) + ' days with stats',
    'n-conversion-queue': dpK(totals.queued) + ' queued',
    'n-meta': dpK(totals.metaDelivered) + ' delivered',
    'n-ttd': dpK(totals.ttdDelivered) + ' delivered',
  };
  const byEventDate = 'event date (global)';
  const historyTips: Partial<Record<DpNodeId, Record<string, string>>> = {
    'web-pixel': { events: dpN(totals.web) + short(truncated.web) },
    'sgtm': { events: dpN(totals.server) + short(truncated.web) },
    'pixel-edge': { events: dpN(totals.events) + short(truncated.web) },
    'batch-ingest': { processed: dpN(totals.events) + short(truncated.web) },
    'store-web-events': { rows: dpN(totals.events) + short(truncated.web), purchases: dpN(totals.purchases) },
    'enrich': {
      'person match · purchases': dpRate(totals.matchPct),
      purchases: dpN(totals.purchases),
      'person match · tickets': dpRate(totals.ticketMatchPct),
    },
    'encore-pg': { 'tickets created': dpN(totals.tickets) + short(truncated.tickets) },
    'ingest-jobs': { runs: 'see EncorePro Pipeline' },
    'store-tickets': { tickets: dpN(totals.tickets) + short(truncated.tickets), 'person match': dpRate(totals.ticketMatchPct) },
    'd1-stats': { 'days with stats': dpN(statsDays) + ' of ' + dpN(spanDays), days: 'rollups use UTC days' },
    'conversion-queue': {
      queued: dpN(totals.queued), web: dpN(totals.queuedWeb), tickets: dpN(totals.queuedTicket), by: byEventDate,
    },
    'meta': { delivered: dpN(totals.metaDelivered), failed: dpN(totals.metaFailed), by: byEventDate },
    'ttd': { delivered: dpN(totals.ttdDelivered), failed: dpN(totals.ttdFailed), by: byEventDate },
  };
  return {
    bucket, buckets, hasData, data, totals, comparable, includesToday, rangeLabel,
    floor, floorStates, spanDays, statsDays, truncated, historyTotals, historyTips,
  };
}

export function derivePriorTotals(data: HistData, priorFromIso: string): PriorTotals {
  const t = histTotals(data);
  const floorMs = data.conv.history_floor != null ? Date.parse(data.conv.history_floor) : null;
  return {
    events: t.events, web: t.web, matchPct: t.matchPct,
    tickets: t.tickets, delivered: t.delivered, failed: t.failed, failPct: t.failPct, queued: t.queued,
    convTruncated: floorMs != null && floorMs > Date.parse(priorFromIso),
  };
}

// Copilot chart-bucket anchor plumbing (design §2a). `mkExplain` returns the
// tooltip's Explain handler — or null when the copilot is off, which keeps
// the pre-existing charts byte-identical. `dock` renders the answer panel
// below the owning chart block.
type MkExplain = (host: string, metricLabel: string, lane: string, metric: string) => ((i: number) => void) | null;
type DockFn = (host: string) => ReactNode;

function VolumeCharts({ hist, mkExplain, dock }: { hist: HistDerived; mkExplain: MkExplain; dock: DockFn }) {
  const { buckets, bucket, data } = hist;
  const web = bucketize(data.web.rows, buckets, bucket, (r) => r.source === 'pixel', (r) => num(r.total));
  const srv = bucketize(data.web.rows, buckets, bucket, (r) => r.source === 'pixel_sgtm', (r) => num(r.total));
  const tix = bucketize(data.tickets.rows, buckets, bucket, () => true, (r) => num(r.total));
  const avgEv = (web.reduce((s, v) => s + v, 0) + srv.reduce((s, v) => s + v, 0)) / (buckets.length || 1);
  const avgTix = tix.reduce((s, v) => s + v, 0) / (buckets.length || 1);
  return (
    <div className="dp-charts">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        <section className="panel dp-chart-block">
          <div className="panel-hd">
            <h3>Web events · by source</h3>
            <span className="meta">{dpK(hist.totals.events)} events · ET buckets</span>
          </div>
          <div className="legend" style={{ paddingTop: 10 }}>
            <span><i style={{ background: 'var(--chart-3)' }}/>Web (pixel)</span>
            <span><i style={{ background: 'var(--chart-1)' }}/>Server (pixel_sgtm)</span>
          </div>
          <div className="dp-chart-body" style={{ paddingTop: 0 }}>
            <StackedMetricChart buckets={buckets} bucket={bucket} h={196} avg={avgEv}
              onExplain={mkExplain('vol-web', 'web events', 'web_events', 'total')}
              series={[{ name: 'web', color: 'var(--chart-3)', values: web }, { name: 'server', color: 'var(--chart-1)', values: srv }]}/>
          </div>
        </section>
        {dock('vol-web')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        <section className="panel dp-chart-block">
          <div className="panel-hd">
            <h3>Tickets ingested</h3>
            <span className="meta">{dpK(hist.totals.tickets)} tickets · ET buckets</span>
          </div>
          <div className="legend" style={{ paddingTop: 10 }}>
            <span><i style={{ background: 'var(--chart-2)' }}/>EncorePro tickets · separate unit</span>
          </div>
          <div className="dp-chart-body" style={{ paddingTop: 0 }}>
            <StackedMetricChart buckets={buckets} bucket={bucket} h={196} avg={avgTix}
              onExplain={mkExplain('vol-tix', 'tickets ingested', 'tickets', 'total')}
              series={[{ name: 'tickets', color: 'var(--chart-2)', values: tix }]}/>
          </div>
        </section>
        {dock('vol-tix')}
      </div>
    </div>
  );
}

/**
 * One rate per bucket, `null` where the bucket has nothing to divide by (or
 * is excluded): an empty bucket is a gap in the chart, never a 0% bar.
 */
export function rateSeries(numArr: readonly number[], denArr: readonly number[], exclude?: readonly boolean[]): (number | null)[] {
  return numArr.map((n, i) => (!exclude?.[i] && (denArr[i] ?? 0) > 0 ? (n / denArr[i]!) * 100 : null));
}

/**
 * The headline of a rate tile: Σ numerator / Σ denominator over the included
 * buckets — the rate of the whole range. The mean of the bucket rates it
 * replaced weighted a ten-purchase hour the same as a thousand-purchase one.
 */
export function rateHeadline(numArr: readonly number[], denArr: readonly number[], exclude?: readonly boolean[]): number | null {
  let n = 0;
  let d = 0;
  numArr.forEach((v, i) => {
    if (exclude?.[i]) return;
    n += v;
    d += denArr[i] ?? 0;
  });
  return ratePct(n, d);
}

function QualityGrid({ hist }: { hist: HistDerived }) {
  const { buckets, bucket, data, floorStates } = hist;
  const W = data.web.rows;
  const T = data.tickets.rows;
  const C = data.conv.rows;
  const purchTot = bucketize(W, buckets, bucket, isPurchase, (r) => num(r.total));
  const allTot = bucketize(W, buckets, bucket, () => true, (r) => num(r.total));
  const tixTot = bucketize(T, buckets, bucket, () => true, (r) => num(r.total));
  const ttdDel = bucketize(C, buckets, bucket, (r) => r.target === 'ttd' && isDeliveredStatus(r.status), (r) => num(r.n));
  // The conversions lane is retention-bounded: a deleted bucket is a gap, and
  // the bucket the floor cuts through is partial — both left out of the rate.
  const before = floorStates.map((s) => s === 'before');
  const partial = floorStates.map((s) => s === 'partial');
  const notFull = floorStates.map((s) => s !== 'full');
  // No Explain on these tiles: Explain analyses raw counts across every event
  // type, not the plotted rate, so its answer would be about something else.
  const metrics: Array<{ l: string; num: number[]; den: number[]; note: string; gated?: boolean }> = [
    { l: 'Usable identity · web purchases', num: bucketize(W, buckets, bucket, isPurchase, (r) => num(r.user_ct)), den: purchTot, note: 'user hash present / purchases' },
    { l: 'Contact presence · tickets', num: bucketize(T, buckets, bucket, () => true, (r) => num(r.contact_ct)), den: tixTot, note: 'email or phone present / tickets' },
    { l: 'Price presence', num: bucketize(W, buckets, bucket, isPurchase, (r) => num(r.priced_ct)), den: purchTot, note: 'value and currency present / purchases' },
    { l: 'Person match · purchases', num: bucketize(W, buckets, bucket, isPurchase, (r) => num(r.person_ct)), den: purchTot, note: 'person matched / purchases' },
    { l: 'Identity envelope', num: bucketize(W, buckets, bucket, () => true, (r) => num(r.identity_ct)), den: allTot, note: 'identity envelope present / events' },
    { l: 'UID2 rate', num: bucketize(C, buckets, bucket, (r) => r.target === 'ttd' && isDeliveredStatus(r.status), (r) => num(r.uid2_ct)), den: ttdDel, note: 'uid2 / ttd delivered', gated: true },
  ];
  return (
    <section className="panel dp-chart-block">
      <div className="panel-hd">
        <h3>Quality rates · fixed 0–100% scale</h3>
        <span className="meta">Dashed line = range rate · empty buckets are gaps · ET buckets</span>
      </div>
      <div className="dp-qgrid">
        {metrics.map((m) => {
          const exclude = m.gated ? notFull : undefined;
          const headline = rateHeadline(m.num, m.den, exclude);
          return (
            <div className="dp-q" key={m.l}>
              <div className="dp-q-hd"><span className="l" title={m.note}>{m.l}</span><span className="v">{dpRate(headline)}</span></div>
              <StackedMetricChart buckets={buckets} bucket={bucket} w={330} h={86} yMax={100} avg={headline} showPeak={false}
                gaps={m.gated ? before : null} partial={m.gated ? partial : null}
                series={[{ name: m.l, color: 'var(--chart-4)', values: rateSeries(m.num, m.den, exclude) }]}/>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function ConversionsCharts({ hist }: { hist: HistDerived }) {
  const { buckets, bucket, data, floor, floorStates } = hist;
  const C = data.conv.rows;
  const gaps = floorStates.map((s) => s === 'before');
  const partial = floorStates.map((s) => s === 'partial');
  const mk = (target: string) => [
    {
      name: 'delivered', color: 'var(--chart-4)',
      values: bucketize(C, buckets, bucket, (r) => r.target === target && isDeliveredStatus(r.status), (r) => num(r.n))
        .map((v, i) => (gaps[i] ? 0 : v)),
    },
    {
      name: 'failed', color: 'var(--bad)',
      values: bucketize(C, buckets, bucket, (r) => r.target === target && isFailedStatus(r.status), (r) => num(r.n))
        .map((v, i) => (gaps[i] ? 0 : v)),
    },
  ];
  const gapCount = gaps.filter(Boolean).length;
  const partialCount = partial.filter(Boolean).length;
  return (
    <section className="panel dp-chart-block">
      <div className="panel-hd">
        <h3>Server-side conversions · delivered vs failed · by event date</h3>
        <span className="meta">
          {gapCount > 0 && floor
            ? gapCount + ' buckets before the history floor (' + fmtDate(floor) + ')'
            : dpK(hist.totals.delivered) + ' delivered'}
          {' · web ' + dpK(hist.totals.queuedWeb) + ' · tickets ' + dpK(hist.totals.queuedTicket) + ' queued'}
        </span>
      </div>
      <div className="dp-qgrid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        {([['meta', 'Meta CAPI'], ['ttd', 'The Trade Desk']] as const).map(([t, label]) => (
          <div className="dp-q" key={t}>
            <div className="dp-q-hd">
              <span className="l">{label}</span>
              <span className="v">{dpK(t === 'meta' ? hist.totals.metaDelivered : hist.totals.ttdDelivered)} del</span>
            </div>
            <StackedMetricChart buckets={buckets} bucket={bucket} w={500} h={140} gaps={gaps} partial={partial} series={mk(t)}/>
          </div>
        ))}
      </div>
      {(gapCount > 0 || partialCount > 0) && (
        <div className="dp-gap-note" style={{ padding: '0 16px 12px' }}>
          {gapCount > 0 && '▨ Grayed buckets predate the conversions history floor: their rows were deleted. '}
          {partialCount > 0 && '◫ The outlined bucket holds the floor: part of it was deleted, so its counts are short. '}
          Delivery logs are retention-bounded; web-event history is not.
        </div>
      )}
    </section>
  );
}

/** The row-cap notice: a lane whose response was cut has short totals. */
export function TruncatedNotice({ web, tickets }: { web: boolean; tickets: boolean }) {
  if (!web && !tickets) return null;
  const lanes = [web && 'web events', tickets && 'tickets'].filter(Boolean).join(' and ');
  return (
    <div role="status" className="dp-gap-note" style={{
      padding: '8px 14px', border: '1px solid color-mix(in oklch, var(--warn) 40%, transparent)',
      background: 'color-mix(in oklch, var(--warn) 8%, transparent)', borderRadius: 'var(--r)',
    }}>
      ⚠ The {lanes} history hit its row limit: the totals and charts for this range are short. Pick a shorter range or a coarser grain.
    </div>
  );
}

function HistorySection({ hist, productionId, resetKey }: {
  hist: HistDerived;
  productionId: string | null;
  /** chartDockResetKey of the ACTIVE history query — the dock's reset input. */
  resetKey: string;
}) {
  // Copilot chart-bucket anchor: one dock at a time, owned by the section so
  // the volume blocks share it. Quality and conversions charts keep the plain
  // tooltip (the explain contract cannot analyse a rate, nor the conversions lane).
  const copilotOn = useCopilotStatus().data?.enabled === true;
  const [explain, setExplain] = useState<ChartExplainSel | null>(null);
  // A new range/scope/grain repaints the buckets — the docked bucket no
  // longer matches what's on screen, so the dock closes. Keyed on the REAL
  // re-scope identity (from/to/grain/production), never on `hist` object
  // identity: the parent re-renders every 5s (clock tick + snapshot polls)
  // and identity churn must not close an open dock mid-generation.
  useEffect(() => { setExplain(null); }, [resetKey]);

  const mkExplain: MkExplain = useCallback((host, metricLabel, lane, metric) => {
    if (!copilotOn) return null;
    return (i: number) => {
      const bucket = hist.buckets[i];
      if (bucket == null) return;
      setExplain({
        host,
        metric: metricLabel,
        bucketLabel: bucketLabel(bucket, hist.bucket),
        request: chartExplainRequest({ lane, metric, bucket, granularity: hist.bucket, productionId }),
        ts: Date.now(),
      });
    };
  }, [copilotOn, hist, productionId]);

  const dock: DockFn = (host) => (explain && explain.host === host
    ? <CPChartDock sel={explain} onClose={() => setExplain(null)}/>
    : null);

  const notice = <TruncatedNotice web={hist.truncated.web} tickets={hist.truncated.tickets}/>;
  // The axis is always fully enumerated now — emptiness means "no rows".
  if (!hist.hasData) {
    return <>{notice}<section className="panel"><div className="dp-empty">No events in this range</div></section></>;
  }
  return (
    <>
      {notice}
      <VolumeCharts hist={hist} mkExplain={mkExplain} dock={dock}/>
      <QualityGrid hist={hist}/>
      <ConversionsCharts hist={hist}/>
    </>
  );
}

// ─── the screen ──────────────────────────────────────────────────────────────

interface DateRange {
  preset: string;
  start: Date;
  end: Date;
}

function isoDayStart(d: Date): string {
  return nyStartOfDayIso(d);
}

/** The range's exclusive end: the New York midnight after its last day (the server reads `< to`). */
function isoDayEndExclusive(d: Date): string {
  return new Date(rangeEndExclusiveMs(d)).toISOString();
}

const GRAIN_OPTIONS: DropdownOption<FlowHistoryGranularity>[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'hour', label: 'Hour' },
  { id: 'day', label: 'Day' },
];

/**
 * The production the URL should name: the one it names when the catalog holds it,
 * else a restricted user's first production the catalog holds, else none (every
 * production, for an unrestricted user). A fixed point: applying it to its own
 * answer gives the same answer, so the effect that applies it settles in one step.
 */
export function productionParam(
  production: string | undefined,
  known: ReadonlySet<string>,
  scoped: readonly string[] | null,
): string | undefined {
  if (production && known.has(production)) return production;
  return scoped?.find((id) => known.has(id));
}

export function DataPipelineView(
  { trace, mode = 'live', production, node }: {
    trace?: string;
    mode?: 'live' | 'history';
    production?: string;
    node?: DpNodeId;
  } = {},
) {
  const nowMs = useNowMs(5000);
  const navigate = useNavigate({ from: '/backend/data-pipeline' });
  // Written to the URL alongside `trace`, which has always lived there.
  const setMode = useCallback(
    (next: 'live' | 'history') => void navigate({ search: (p) => ({ ...p, mode: next }) }),
    [navigate],
  );
  // The open card lives in the URL, so a reload or a shared link reopens it.
  const selected = node ?? null;
  const setSelected = useCallback(
    (id: DpNodeId | null) =>
      void navigate({ search: (p) => ({ ...p, node: id ?? undefined }), replace: true }),
    [navigate],
  );

  // TRACE mode — a correlation_id in the URL activates the E2E lineage overlay.
  const traceId = trace ?? null;
  const setTrace = useCallback(
    (id: string | null) =>
      void navigate({ search: (p) => ({ ...p, trace: id ?? undefined }) }),
    [navigate],
  );
  const lineageQ = useFlowLineage(traceId);
  // The trace's selected step rings its node in the diagram; a node clicked in
  // the diagram while tracing selects that node's newest step. Neither writes
  // the page's own open card, which is kept for when the trace closes.
  const [traceFocus, setTraceFocus] = useState<DpNodeId | null>(null);
  const [tracePick, setTracePick] = useState<{ node: DpNodeId; n: number } | null>(null);
  const onDiagramSelect = useCallback(
    (id: DpNodeId | null) => {
      if (traceId) {
        if (id) setTracePick((prev) => ({ node: id, n: (prev?.n ?? 0) + 1 }));
        return;
      }
      setSelected(id);
    },
    [traceId, setSelected],
  );
  const traceOverlay = useMemo(() => {
    if (!traceId) return null;
    return dpTraceDerive(lineageQ.data?.rows ?? [], kindFromCorrelationId(traceId), lineageQ.data?.destinations ?? [], lineageQ.data?.as_of);
  }, [traceId, lineageQ.data]);
  // A production-restricted user has no "all productions" view — the server
  // refuses the unscoped read rather than silently widening it, so defaulting to
  // 'all' would 400 every panel on first paint. Start on the user's own scope.
  const me = useMe();
  const scopedProductions = me.data?.productions ?? null;
  // The production filter lives in the URL (`?production=`), so a reload or a
  // shared link keeps it.
  const prod = production ?? 'all';
  const setProd = useCallback(
    (next: string) =>
      void navigate({
        search: (p) => ({ ...p, production: next === 'all' ? undefined : next }),
        replace: true,
      }),
    [navigate],
  );

  const [range, setRange] = useState<DateRange>(() => ({ preset: 'last30', ...resolveRange('last30', new Date()) }));
  const [gran, setGran] = useState<FlowHistoryGranularity>('auto');
  const [paused, setPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState<string | null>(null);
  const [chan, setChan] = useState('all');
  const [etype, setEtype] = useState('all');
  const [pixel, setPixel] = useState('all');

  const canReveal = useCan('pii_reveal', 'full');
  const canLogs = useCan('logs_view', 'own');
  const productionsQ = useProductions();
  const productions = useMemo(() => productionsQ.data ?? [], [productionsQ.data]);
  // One rule for the production in the URL, once the catalog and the session are
  // known: a catalog id stays; anything else (a stale or edited link, or none)
  // becomes the restricted user's first production the catalog holds, or for an
  // unrestricted user every production. It converges in one step: its own target
  // always satisfies it.
  const sessionKnown = me.isSuccess;
  useEffect(() => {
    if (!productionsQ.isSuccess || !sessionKnown) return;
    const target = productionParam(production, new Set(productions.map((p) => p.id)), scopedProductions);
    if (target !== production) setProd(target ?? 'all');
  }, [production, productionsQ.isSuccess, productions, scopedProductions, sessionKnown, setProd]);

  const productionId = prod === 'all' ? null : prod;
  const scoped = productionId != null;
  const prodLabel = scoped ? (productions.find((p) => p.id === prod)?.name ?? prod) : 'All productions';

  const snapshotQ = useFlowSnapshot(productionId);
  const snapshot = snapshotQ.data;
  const tapEnabled = snapshot ? snapshot.batch.tap.enabled : true;

  const feed = useFlowFeed(
    { productionId, channel: chan as 'all' | 'web' | 'server', eventType: etype, pixelId: pixel },
    paused,
    mode === 'live' && tapEnabled && !traceId,
  );

  // HISTORY reads — current range + the prior window its deltas compare with:
  // as many completed New York days, immediately before the range. The end
  // bound is the exclusive next NY midnight, as the server reads `< to`.
  const histQuery: FlowHistoryQuery | null = mode === 'history'
    ? { from: isoDayStart(range.start), to: isoDayEndExclusive(range.end), granularity: gran, productionId }
    : null;
  const histQ = useFlowHistory(histQuery);
  // ONE New York "today" for both windows: the day the loaded history was
  // COMPUTED (`historyReadDayMs`). The prior window and the current totals'
  // completed-day cutoff move together, and only with the data, so a page left
  // open across midnight never counts a partial day as complete (C1-3).
  const readDayMs = historyReadDayMs(histQ.data, nowMs);
  const prior = useMemo(() => priorWindow(range, readDayMs), [range, readDayMs]);
  const refetchHistory = histQ.refetch;
  // While the loaded data predates today in New York, read every lane again
  // once a minute: the server caches a recent range for 60 s, so the read
  // after that returns observations from after midnight.
  const rolloverTick = mode === 'history' && historyNeedsRollover(readDayMs, nowMs) ? Math.floor(nowMs / 60_000) : null;
  useEffect(() => {
    if (rolloverTick != null) refetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch is a fresh closure each render; the minute tick is the trigger
  }, [rolloverTick]);
  const priorQuery: FlowHistoryQuery | null = mode === 'history' && prior
    ? { from: prior.from, to: prior.to, granularity: 'day', productionId }
    : null;
  const priorQ = useFlowHistory(priorQuery);
  const hist = useMemo(() => (histQ.data ? deriveHistory(histQ.data, range, readDayMs) : null), [histQ.data, range, readDayMs]);
  const priorTotals = useMemo(
    () => (prior && priorQ.data ? derivePriorTotals(priorQ.data, prior.from) : null),
    [priorQ.data, prior],
  );

  // Diagram selection pre-filters the feed (§3): source nodes PRE-SET the
  // channel; selecting anything else (or deselecting) leaves a manually
  // chosen channel filter untouched.
  useEffect(() => {
    if (selected === 'sgtm') setChan('server');
    else if (selected === 'web-pixel') setChan('web');
  }, [selected]);

  // Escape closes the open card, but not while a trace is open: there it closes
  // only the trace's own popovers, and the card waits for the trace to close.
  const traceOpenRef = useRef(false);
  traceOpenRef.current = !!traceId;
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !traceOpenRef.current) setSelected(null);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [setSelected]);

  const doPause = () => { setPaused(true); setPausedAt(new Date().toISOString()); };
  const doResume = () => { setPaused(false); setPausedAt(null); };

  // House DateFilter resolves presets against a pinned mock date — re-resolve
  // against the real clock so HISTORY ranges are honest.
  const handleRange = (v: DateRange) => {
    setRange(v.preset !== 'custom' ? { preset: v.preset, ...resolveRange(v.preset, new Date()) } : v);
  };

  const dateLabel = range.preset === 'custom'
    ? `${fmtRangeDate(range.start)} – ${fmtRangeDate(range.end)}`
    : (DATE_PRESETS.find((p: { id: string; label: string }) => p.id === range.preset)?.label ?? 'Custom');
  const grainLabel = GRAIN_OPTIONS.find((g) => g.id === gran)?.label ?? 'Auto';

  // A restricted user is never offered "All productions": the server refuses it.
  const prodOptions = [
    ...(scopedProductions ? [] : [{ id: 'all', label: 'All productions' }]),
    ...productions
      .map((p) => ({ id: p.id, label: p.name }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })),
  ];

  const histLoading = mode === 'history' && histQ.isLoading;
  const histError = mode === 'history' && histQ.isError && !histQ.data;

  return (
    <div className="dp-scroll">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Data Pipeline</h1>
        </div>
        <PageActions className="page-actions dp-hd-tools">
          <SearchableDropdown
            label="PRODUCTION" value={prod} onChange={setProd} options={prodOptions}
            width="auto" popWidth={320} size="sm" searchPlaceholder={`Search ${prodOptions.length} productions…`}
          />
          <ModeSeg mode={mode} onChange={setMode}/>
          {mode === 'history'
            ? <DateFilter value={range} onChange={handleRange}/>
            : <DisabledDd label="DATE" val={dateLabel} icon={Icons.calendar} title="Switch to HISTORY to pick a range"/>}
          {mode === 'history'
            ? <Dropdown label="GRAIN" value={gran} onChange={(g: FlowHistoryGranularity) => setGran(g)} options={GRAIN_OPTIONS} rightAlign/>
            : <DisabledDd label="GRAIN" val={grainLabel} title="Switch to HISTORY to pick a granularity"/>}
          {snapshot && <HealthChip data-phone="inline" snapshot={snapshot}/>}
          <TraceBox trace={traceId} onTrace={setTrace}/>
        </PageActions>
      </div>

      {!snapshot && snapshotQ.isLoading && (
        <div className="muted" style={{ padding: 40, textAlign: 'center', fontSize: 12 }}>Loading pipeline snapshot…</div>
      )}
      {!snapshot && snapshotQ.isError && (
        <QueryErrorNote error={snapshotQ.error} what="the pipeline snapshot" onRetry={() => void snapshotQ.refetch()}/>
      )}

      {snapshot && (
        <>
          <KpiStrip snapshot={snapshot} mode={mode} hist={hist} prior={priorTotals} scoped={scoped}/>

          {snapshotQ.isError && (
            <div style={{ padding: '8px 22px', borderBottom: '1px solid var(--line)', background: 'color-mix(in oklch, var(--warn) 8%, transparent)' }}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 700 }}>
                ⚠ Snapshot refresh failed — showing the last good snapshot. Retrying automatically.
              </span>
            </div>
          )}

          <div className="dp-body">
            <section className="panel">
              <div className="panel-hd" style={{ padding: '10px 16px' }}>
                <h3>System flow</h3>
                <span className="meta">
                  {scoped ? 'Scoped · ' + prodLabel + ' · ' : ''}
                  {mode === 'live'
                    ? `${DP_NODES.length} nodes · ${DP_EDGES.length} edges · snapshot ` + withEt(dpClock(snapshot.as_of))
                    : 'Totals · ' + (hist ? hist.rangeLabel : '')}
                </span>
              </div>
              <div style={{ padding: '14px 10px 8px' }}>
                <FlowDiagram
                  snapshot={snapshot} mode={mode} nowMs={nowMs}
                  historyTotals={hist ? hist.historyTotals : null}
                  historyTips={hist ? hist.historyTips : null}
                  rangeLabel={hist ? hist.rangeLabel : ''}
                  selected={traceId ? traceFocus : selected} onSelect={onDiagramSelect}
                  toggle={!traceId}
                  scoped={scoped} trace={traceOverlay}
                />
              </div>
            </section>

            {traceId && (
              <LineageTrace
                correlationId={traceId} query={lineageQ}
                onExit={() => setTrace(null)}
                onFocusNode={setTraceFocus} pick={tracePick}
              />
            )}
            {/* Kept mounted while a trace is open, so closing it returns to the same
                card, expanded rows, filters and history range. */}
            <div className="dp-panels" hidden={!!traceId}>
            {selected && (
              <ContextPanel
                selected={selected} snapshot={snapshot} nowMs={nowMs}
                productionId={productionId} prodLabel={prodLabel}
                feedPage={feed.page}
                canReveal={canReveal} canLogs={canLogs} scoped={scoped}
                onClose={() => setSelected(null)} onTrace={setTrace}
              />
            )}

            {mode === 'live' ? (
              <Inspector
                snapshot={snapshot} feed={feed}
                paused={paused} pausedAt={pausedAt} onPause={doPause} onResume={doResume}
                scoped={scoped} prodLabel={prodLabel} onClearProd={() => setProd('all')}
                chan={chan} setChan={setChan} etype={etype} setEtype={setEtype} pixel={pixel} setPixel={setPixel}
                canReveal={canReveal} tapEnabled={tapEnabled} onTrace={setTrace}
              />
            ) : histLoading ? (
              <div className="muted" style={{ padding: 40, textAlign: 'center', fontSize: 12 }}>Loading pipeline history…</div>
            ) : histError ? (
              <QueryErrorNote error={histQ.error} what="pipeline history" onRetry={histQ.refetch}/>
            ) : hist ? (
              <HistorySection hist={hist} productionId={productionId}
                resetKey={histQuery ? chartDockResetKey(histQuery) : ''}/>
            ) : null}
            </div>
          </div>

          <div className="dp-foot">
            {mode === 'live'
              ? `LIVE · SNAPSHOT AS OF ${withEt(dpClock(snapshot.as_of))} (${dpAge(snapshot.as_of, nowMs)}) · ${snapshot.display_policy ? `CACHE ≤${snapshot.display_policy.snapshot_cache_ttl_secs}S · ` : ''}SNAPSHOT POLL ${FLOW_SNAPSHOT_POLL_MS / 1000}S · FEED POLL ${FLOW_FEED_POLL_MS / 1000}S`
              : 'TOTALS · ' + (hist ? hist.rangeLabel : '')}
            {scoped ? ' · SCOPED · ' + prodLabel.toUpperCase() : ''}
          </div>
        </>
      )}
    </div>
  );
}
